import { useEffect, useRef, useState } from "react";
import {
  FiArrowRight,
  FiAward,
  FiCheck,
  FiCompass,
  FiPlayCircle,
  FiShield,
  FiStar,
  FiUsers,
  FiWind,
  FiX,
} from "react-icons/fi";
import "./CommercialSite.css";

const publicAsset = (fileName) => `${process.env.PUBLIC_URL}/${fileName}`;

const bookingWidgetConfig = {
  slug: process.env.REACT_APP_PILCORE_BOOKING_SLUG || "hotel-travesia",
  bookingBaseUrl:
    process.env.REACT_APP_PILCORE_BOOKING_BASE_URL || "https://app.pilcore.com/booking",
  scriptUrl:
    process.env.REACT_APP_PILCORE_BOOKING_WIDGET_URL ||
    (process.env.NODE_ENV === "development"
      ? "http://localhost:3020/widgets/pilcore-booking-widget.js"
      : "https://app.pilcore.com/widgets/pilcore-booking-widget.js"),
};

const assets = {
  logo: publicAsset("logo_hotel_travesia.png"),
  heroVideo: publicAsset("5498751-uhd_3840_2160_25fps.mp4"),
  cinematicVideo: publicAsset("5137835-uhd_4096_2160_25fps.mp4"),
  cinematicVideoSecondary: publicAsset("5378931-uhd_4096_2160_25fps.mp4"),
  portraitVideo: publicAsset("12712645_2160_3840_30fps.mp4"),
  service: publicAsset("pexels-cottonbro-6466479.jpg"),
  superior: publicAsset("pexels-digitalbuggu-172872.jpg"),
  suite: publicAsset("pexels-enginakyurt-2952663.jpg"),
  standard: publicAsset("pexels-frdsmu-32829704.jpg"),
  individual: publicAsset("pexels-markusspiske-26139.jpg"),
  family: publicAsset("pexels-meng-soon-ng-2151620101-31767049.jpg"),
  detail: publicAsset("pexels-pixabay-271672.jpg"),
};

const viewportBreakpoints = [
  { max: 767, type: "mobile", compactHeight: 740 },
  { max: 1023, type: "tablet", compactHeight: 820 },
  { max: 1439, type: "laptop", compactHeight: 860 },
  { max: 1919, type: "desktop", compactHeight: 940 },
  { max: Number.POSITIVE_INFINITY, type: "wide", compactHeight: 1040 },
];

const getViewportProfile = (width, height) => {
  const breakpoint = viewportBreakpoints.find(({ max }) => width <= max) ?? viewportBreakpoints[3];

  return {
    type: breakpoint.type,
    height: height <= breakpoint.compactHeight ? "compact" : "regular",
  };
};

const quickHighlights = [
  {
    title: "Cinco formas de alojarte",
    description:
      "Desde la habitacion individual hasta la Suite Travesia, con una propuesta clara para cada tipo de viaje.",
    Icon: FiUsers,
  },
  {
    title: "Sobriedad que seduce",
    description:
      "Azules profundos, marfil calido y acentos dorados para una percepcion premium y muy cuidada.",
    Icon: FiStar,
  },
  {
    title: "Reserva con intencion",
    description:
      "CTAs visibles y una seleccion rapida para empujar la eleccion hacia la estancia ideal.",
    Icon: FiCompass,
  },
];

const rooms = [
  {
    id: "individual",
    name: "Individual",
    badge: "Estancia esencial",
    image: assets.individual,
    intro:
      "La opcion mas intima para viajar ligero sin perder la sensacion de estar en un hotel con identidad.",
    details: [
      "Viajes agiles y escapadas cortas",
      "Silencio visual para descansar mejor",
      "Entrada elegante al universo Travesia",
    ],
    cta: "Elegir habitacion individual",
  },
  {
    id: "doble-estandar",
    name: "Doble estandar",
    badge: "La eleccion segura",
    image: assets.standard,
    intro:
      "Equilibrio entre confort, serenidad y una estetica limpia para quien quiere acertar desde el primer clic.",
    details: [
      "Pensada para dos",
      "Descanso confortable y proporcionado",
      "Una base impecable para desconectar",
    ],
    cta: "Reservar doble estandar",
  },
  {
    id: "doble-superior",
    name: "Doble superior",
    badge: "Subir de nivel",
    image: assets.superior,
    intro:
      "Mas presencia, mas amplitud percibida y una atmosfera que invita a elegir una categoria superior.",
    details: [
      "Ideal para escapadas de pareja",
      "Sensacion de luz, calma y cuidado",
      "Una estancia que se siente mas especial",
    ],
    cta: "Quiero la doble superior",
  },
  {
    id: "familiar",
    name: "Familiar",
    badge: "Compartir con estilo",
    image: assets.family,
    intro:
      "Espacio pensado para convivir con comodidad sin renunciar al orden, la armonia ni la sensacion premium.",
    details: [
      "Perfecta para viajar acompanados",
      "Distribucion amable y versatil",
      "Comodidad compartida con elegancia",
    ],
    cta: "Escoger habitacion familiar",
  },
  {
    id: "suite-travesia",
    name: "Suite Travesia",
    badge: "Signature stay",
    image: assets.suite,
    intro:
      "La habitacion mas aspiracional de la casa: la que convierte la curiosidad inicial en deseo claro de reservar lo mejor.",
    details: [
      "La categoria con mas magnetismo",
      "Perfecta para momentos especiales",
      "La forma mas alta de vivir Travesia",
    ],
    cta: "Reservar la Suite Travesia",
    featured: true,
  },
];

const experienceCards = [
  {
    title: "Llegada impecable",
    description:
      "Textiles bien presentados, composicion ordenada y una sensacion inmediata de cuidado real.",
    Icon: FiShield,
  },
  {
    title: "Detalles que enamoran",
    description:
      "Pequenas cortesias y gestos sutiles que elevan la percepcion de la estancia desde el primer vistazo.",
    Icon: FiAward,
  },
  {
    title: "Diseno que descansa",
    description:
      "Una paleta sobria y un ritmo visual sereno para que el lujo se sienta natural, no forzado.",
    Icon: FiWind,
  },
  {
    title: "Decision sin friccion",
    description:
      "Cada bloque empuja con claridad hacia la siguiente accion para que reservar resulte casi inevitable.",
    Icon: FiCompass,
  },
];

function BookingWidget({ variant = "modal" }) {
  const [loadFailed, setLoadFailed] = useState(false);
  const isHeader = variant === "header";

  useEffect(() => {
    if (window.customElements?.get("pilcore-booking-widget")) return undefined;

    const existingScript = document.querySelector("script[data-pilcore-booking-widget]");
    if (existingScript) {
      const handleError = () => setLoadFailed(true);
      existingScript.addEventListener("error", handleError);
      return () => existingScript.removeEventListener("error", handleError);
    }

    const script = document.createElement("script");
    script.src = bookingWidgetConfig.scriptUrl;
    script.defer = true;
    script.dataset.pilcoreBookingWidget = "true";
    script.addEventListener("error", () => setLoadFailed(true));
    document.head.appendChild(script);
    return undefined;
  }, []);

  const fallbackUrl = `${bookingWidgetConfig.bookingBaseUrl.replace(/\/$/, "")}/${
    bookingWidgetConfig.slug
  }?lang=es`;

  if (loadFailed) {
    return (
      <a className="travesia-button travesia-button--primary" href={fallbackUrl}>
        Abrir motor de reservas
        <FiArrowRight aria-hidden="true" />
      </a>
    );
  }

  return (
    <pilcore-booking-widget
      slug={bookingWidgetConfig.slug}
      lang="es"
      booking-base-url={bookingWidgetConfig.bookingBaseUrl}
      max-adults="6"
      max-children="4"
      density={isHeader ? "compact" : undefined}
      style={{
        "--pilcore-widget-accent": "#a77b37",
        "--pilcore-widget-text": "#17232d",
        "--pilcore-widget-muted": "#52616d",
        "--pilcore-widget-radius": "8px",
        "--pilcore-widget-shadow": isHeader
          ? "none"
          : "0 22px 55px rgba(5, 13, 20, 0.28)",
      }}
    >
      <a href={fallbackUrl}>Abrir motor de reservas</a>
    </pilcore-booking-widget>
  );
}

function CommercialSite() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const bookingCloseRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [viewport, setViewport] = useState(() => {
    if (typeof window === "undefined") {
      return { type: "desktop", height: "regular" };
    }

    return getViewportProfile(window.innerWidth, window.innerHeight);
  });

  useEffect(() => {
    const syncViewport = () => {
      const nextViewport = getViewportProfile(window.innerWidth, window.innerHeight);

      setViewport((currentViewport) => {
        if (
          currentViewport.type === nextViewport.type &&
          currentViewport.height === nextViewport.height
        ) {
          return currentViewport;
        }

        return nextViewport;
      });
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);

    return () => {
      window.removeEventListener("resize", syncViewport);
    };
  }, []);

  useEffect(() => {
    if (!bookingOpen) return undefined;

    previousFocusRef.current = document.activeElement;
    document.body.classList.add("travesia-bookingOpen");

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setBookingOpen(false);
    };

    window.addEventListener("keydown", closeOnEscape);
    window.requestAnimationFrame(() => bookingCloseRef.current?.focus());

    return () => {
      document.body.classList.remove("travesia-bookingOpen");
      window.removeEventListener("keydown", closeOnEscape);
      previousFocusRef.current?.focus?.();
    };
  }, [bookingOpen]);

  const openBookingFromLink = (event) => {
    const bookingLink = event.target.closest?.('a[href="#reserva-directa"]');
    if (!bookingLink) return;

    event.preventDefault();
    setBookingOpen(true);
  };

  return (
    <div
      className="travesia-page"
      data-viewport={viewport.type}
      data-height={viewport.height}
      onClick={openBookingFromLink}
    >
      <a className="travesia-skipLink" href="#contenido-principal">
        Ir al contenido
      </a>

      <header className="travesia-header">
        <div className="travesia-header__primary">
          <a className="travesia-brand" href="#hero" aria-label="Volver al inicio de Hotel Travesia">
            <img src={assets.logo} alt="Logo Hotel Travesia" />
          </a>

          <nav className="travesia-nav" aria-label="Secciones principales">
            <a href="#habitaciones">Habitaciones</a>
            <a href="#experiencia">Experiencia</a>
            <a href="#galeria">Galeria</a>
            <a href="#reserva-directa">Reserva</a>
          </nav>
        </div>

        <div
          className="travesia-header__booking"
          role="region"
          aria-label="Motor de reservas"
        >
          <BookingWidget variant="header" />
        </div>
      </header>

      <main id="contenido-principal" className="travesia-main">
        <section className="hero" id="hero">
          <div className="hero__videoShell" aria-hidden="true">
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              poster={assets.superior}
              className="hero__video"
            >
              <source src={assets.heroVideo} type="video/mp4" />
            </video>
          </div>

          <div className="hero__veil" />

          <div className="hero__content">
            <div className="hero__copy">
              <span className="travesia-eyebrow">Elegancia serena · Reserva directa</span>
              <h1>La estancia que convierte una reserva en una decision emocional.</h1>
              <p className="hero__lead">
                Hotel Travesia une sobriedad contemporanea, contraste elegante y una coleccion de
                habitaciones pensadas para vender descanso, amplitud y deseo de subir de categoria.
              </p>

              <div className="hero__actions">
                <a className="travesia-button travesia-button--primary" href="#habitaciones">
                  Escoger mi habitacion
                  <FiArrowRight aria-hidden="true" />
                </a>
                <a className="travesia-button travesia-button--secondary" href="#suite-travesia">
                  Ver Suite Travesia
                </a>
              </div>

              <div className="hero__microPoints" aria-label="Puntos destacados">
                <span>
                  <FiCheck aria-hidden="true" />
                  De individual a suite
                </span>
                <span>
                  <FiCheck aria-hidden="true" />
                  Estilo sobrio y contemporaneo
                </span>
                <span>
                  <FiCheck aria-hidden="true" />
                  CTA claros en todo momento
                </span>
              </div>
            </div>

            <aside className="hero__panel">
              <div className="hero__panelTag">Seleccion destacada</div>
              <img src={assets.suite} alt="Suite Travesia con desayuno servido en la habitacion" />

              <div className="hero__panelBody">
                <h2>Suite Travesia</h2>
                <p>
                  La estancia mas deseada del hotel. Mas presencia, mas recuerdo y una promesa
                  aspiracional muy clara desde el primer vistazo.
                </p>

                <ul className="hero__panelList">
                  <li>
                    <FiCheck aria-hidden="true" />
                    La habitacion que mas invita a reservar en grande
                  </li>
                  <li>
                    <FiCheck aria-hidden="true" />
                    Perfecta para una escapada especial
                  </li>
                  <li>
                    <FiCheck aria-hidden="true" />
                    El cierre premium del recorrido comercial
                  </li>
                </ul>

                <a
                  className="travesia-button travesia-button--primary hero__panelCta"
                  href="#suite-travesia"
                >
                  Quiero descubrirla
                  <FiArrowRight aria-hidden="true" />
                </a>
              </div>
            </aside>
          </div>

          <div className="hero__highlights">
            {quickHighlights.map(({ title, description, Icon }) => (
              <article key={title} className="highlightCard">
                <div className="highlightCard__icon">
                  <Icon aria-hidden="true" />
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="travesia-section" id="habitaciones">
          <div className="sectionHeading">
            <div>
              <span className="travesia-eyebrow">Habitaciones</span>
              <h2 className="sectionHeading__title sectionHeading__title--compact">
                Cada categoria esta disenada para ser la correcta y, a la vez, tentar a subir un
                nivel mas.
              </h2>
            </div>

            <p>
              La navegacion acompana al visitante desde la opcion mas agil hasta la mas aspiracional
              para que elegir estancia sea facil, emocionante y claramente orientado a reservar.
            </p>
          </div>

          <div className="roomsGrid">
            {rooms.map(({ id, name, badge, image, intro, details, cta, featured }) => (
              <article
                key={id}
                id={id}
                className={`roomCard${featured ? " roomCard--featured" : ""}`}
              >
                <div className="roomCard__media">
                  <img src={image} alt={`Habitacion ${name} de Hotel Travesia`} loading="lazy" />
                </div>

                <div className="roomCard__content">
                  <div className="roomCard__topline">
                    <span className="roomCard__badge">{badge}</span>
                    <span className="roomCard__anchor">Reserva directa</span>
                  </div>

                  <h3>{name}</h3>
                  <p>{intro}</p>

                  <ul className="roomCard__details">
                    {details.map((detail) => (
                      <li key={detail}>
                        <FiCheck aria-hidden="true" />
                        {detail}
                      </li>
                    ))}
                  </ul>

                  <div className="roomCard__footer">
                    <span>Escoge esta estancia y sigue hacia tu reserva.</span>
                    <a
                      className="travesia-button travesia-button--primary roomCard__ctaButton"
                      href="#reserva-directa"
                    >
                      {cta}
                      <FiArrowRight aria-hidden="true" />
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="travesia-section" id="experiencia">
          <div className="sectionHeading">
            <div>
              <span className="travesia-eyebrow">Experiencia</span>
              <h2>
                La confianza tambien se construye con detalles que el huesped percibe al instante.
              </h2>
            </div>

            <p>
              Servicio, preparacion de la habitacion, cortesia y calma visual: todo suma para que la
              estancia parezca mejor incluso antes del check-in.
            </p>
          </div>

          <div className="experienceLayout">
            <div className="experienceImages">
              <article className="imagePanel imagePanel--service">
                <img
                  src={assets.service}
                  alt="Servicio preparando una cama impecable en Hotel Travesia"
                  loading="lazy"
                />
                <div className="imagePanel__caption">
                  <span>Servicio impecable</span>
                  <p>La sensacion de cuidado empieza mucho antes de entrar en la habitacion.</p>
                </div>
              </article>

              <article className="imagePanel imagePanel--detail">
                <img
                  src={assets.detail}
                  alt="Detalle de cortesia con tazas y agua en una habitacion"
                  loading="lazy"
                />
                <div className="imagePanel__caption">
                  <span>Pequenos gestos, gran recuerdo</span>
                  <p>La hospitalidad se vuelve tangible cuando cada rincon esta pensado con intencion.</p>
                </div>
              </article>
            </div>

            <div className="experienceCards">
              {experienceCards.map(({ title, description, Icon }) => (
                <article key={title} className="experienceCard">
                  <div className="experienceCard__icon">
                    <Icon aria-hidden="true" />
                  </div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="travesia-section travesia-section--cinema" id="galeria">
          <div className="sectionHeading sectionHeading--light">
            <div>
              <span className="travesia-eyebrow">Galeria en movimiento</span>
              <h2>
                Cuando Travesia se ve en video, reservar deja de ser racional y pasa a ser deseo.
              </h2>
            </div>

            <p>
              La atmosfera, la luz y el ritmo visual refuerzan la sensacion de hotel premium y hacen
              que la mejor habitacion parezca la eleccion natural.
            </p>
          </div>

          <div className="cinemaLayout">
            <article className="cinemaCard cinemaCard--wide">
              <div className="cinemaCard__label">
                <FiPlayCircle aria-hidden="true" />
                Recorrido cinematografico
              </div>

              <div className="cinemaCard__mediaStack">
                <video controls preload="metadata" poster={assets.superior} className="cinemaCard__video">
                  <source src={assets.cinematicVideo} type="video/mp4" />
                </video>

                <video
                  controls
                  preload="metadata"
                  poster={assets.standard}
                  className="cinemaCard__video"
                >
                  <source src={assets.cinematicVideoSecondary} type="video/mp4" />
                </video>
              </div>
            </article>

            <div className="cinemaSidebar">
              <article className="cinemaCard cinemaCard--portrait">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  poster={assets.suite}
                  className="cinemaCard__video cinemaCard__video--portrait"
                >
                  <source src={assets.portraitVideo} type="video/mp4" />
                </video>
              </article>

              <article className="cinemaNote">
                <h3>La atmosfera vende antes de explicar.</h3>
                <p>
                  Contraste cuidado, movimiento suave y una puesta en escena que acompana la subida
                  de valor percibido en cada categoria.
                </p>

                <ul className="cinemaNote__list">
                  <li>
                    <FiCheck aria-hidden="true" />
                    Mas emocion desde el primer scroll
                  </li>
                  <li>
                    <FiCheck aria-hidden="true" />
                    Mejor contexto para la suite y las superiores
                  </li>
                  <li>
                    <FiCheck aria-hidden="true" />
                    Imagen de marca solida y memorable
                  </li>
                </ul>

                <a className="travesia-button travesia-button--primary" href="#reserva-directa">
                  Elegir estancia
                  <FiArrowRight aria-hidden="true" />
                </a>
              </article>
            </div>
          </div>
        </section>

        <section className="travesia-section travesia-section--cta" id="reserva-directa">
          <div className="ctaShell">
            <img className="ctaShell__logo" src={assets.logo} alt="" aria-hidden="true" />

            <div className="ctaShell__body">
              <div className="ctaShell__copy">
                <span className="travesia-eyebrow">Reserva directa</span>
                <h2>Haz que la eleccion sea inmediata y que la mejor habitacion parezca irresistible.</h2>
                <p>
                  Todo el recorrido lleva a este punto: escoger la estancia adecuada y avanzar con
                  claridad hacia la reserva. Sin distracciones. Sin dudas.
                </p>
              </div>

              <div className="ctaShell__bookingAction">
                <button
                  type="button"
                  className="travesia-button travesia-button--primary"
                  onClick={() => setBookingOpen(true)}
                >
                  Reservar aquí
                  <FiArrowRight aria-hidden="true" />
                </button>
                <p className="ctaShell__bookingNote">
                  Consulta fechas y ocupación en el motor seguro de Pilcore sin salir de esta web.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {bookingOpen ? (
        <div
          className="bookingModal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setBookingOpen(false);
          }}
        >
          <section
            className="bookingModal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="booking-modal-title"
          >
            <header className="bookingModal__header">
              <div>
                <span className="travesia-eyebrow">Reserva directa</span>
                <h2 id="booking-modal-title">Reserva tu estancia en Hotel Travesía</h2>
              </div>
              <button
                ref={bookingCloseRef}
                type="button"
                className="bookingModal__close"
                aria-label="Cerrar motor de reservas"
                title="Cerrar"
                onClick={() => setBookingOpen(false)}
              >
                <FiX aria-hidden="true" />
              </button>
            </header>
            <div className="bookingModal__widget">
              <BookingWidget />
            </div>
            <p className="bookingModal__note">
              Selecciona fechas y ocupación. La disponibilidad se consultará en el motor seguro de
              Pilcore.
            </p>
          </section>
        </div>
      ) : null}

    </div>
  );
}

export default CommercialSite;
